import { Alert, Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { recommendationGroup, type RecommendationGroup, type ReferralSummary } from '@hv/shared';
import { useMyAllotment, useMyReferrals, useReferralForm } from '../api/hooks';
import { describeError } from '../components/Notify';
import { AllotmentUsageList, ReferralCardList } from '../referrals/ReferralCards';

const SECTIONS: { group: RecommendationGroup; title: string; empty: string }[] = [
  {
    group: 'draft',
    title: 'Draft recommendations',
    empty: 'You have no recommendation drafts in progress.',
  },
  {
    group: 'submitted',
    title: 'Submitted recommendations',
    empty: 'You have not submitted any recommendations yet.',
  },
  { group: 'expired', title: 'Expired recommendations', empty: 'No expired recommendations.' },
];

function Section({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: ReferralSummary[];
  empty: string;
}) {
  const id = `section-${title.toLowerCase().replaceAll(' ', '-')}`;
  return (
    <Box component="section" aria-labelledby={id}>
      <Typography variant="h3" component="h3" id={id} mb={1}>
        {title}
      </Typography>
      <ReferralCardList rows={rows} empty={empty} />
    </Box>
  );
}

export function MyRecommendationsPage() {
  const list = useMyReferrals();
  const usage = useMyAllotment();
  const config = useReferralForm();
  const limit = usage.data?.limit ?? config.data?.allotmentLimit;
  const marked = list.data?.filter((r) => r.countsTowardAllotment) ?? [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h2" mb={1}>
        My Recommendations
      </Typography>
      <Typography color="text.secondary" mb={2}>
        Referrals you marked as one of your allotted Trustee recommendations.
        {limit !== undefined && ` Each academic year allows ${limit} active recommendations.`}{' '}
        Drafts count; archived recommendations don't, and a recommendation stops counting once its
        academic year ends.
      </Typography>
      <Paper component="section" aria-labelledby="allotment-used" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h3" component="h3" id="allotment-used" mb={0.5}>
          Allotment used
        </Typography>
        {usage.isPending ? (
          <Skeleton width="60%" />
        ) : usage.error ? (
          <Alert severity="error">
            Could not load your recommendation usage: {describeError(usage.error)}
          </Alert>
        ) : (
          <AllotmentUsageList cycles={usage.data.cycles} limit={usage.data.limit} />
        )}
      </Paper>
      {list.isPending ? (
        <Stack gap={2}>
          <Skeleton variant="rounded" height={88} />
          <Skeleton variant="rounded" height={88} />
        </Stack>
      ) : list.error ? (
        <Alert severity="error">
          Could not load your recommendations: {describeError(list.error)}
        </Alert>
      ) : (
        <Stack gap={3}>
          {SECTIONS.map(({ group, title, empty }) => {
            const rows = marked.filter((r) => recommendationGroup(r) === group);
            if (group === 'expired' && !rows.length) return null;
            return <Section key={group} title={title} rows={rows} empty={empty} />;
          })}
        </Stack>
      )}
    </Box>
  );
}
